import type { GameId } from "./games";

export const PLAYER_SLOTS = ["A", "B", "C", "D"] as const;
export type PlayerSlot = (typeof PLAYER_SLOTS)[number];
export const MAX_PLAYERS = PLAYER_SLOTS.length;
export type PlayMode = "strokes" | "race";
export type CoursePack = "classic" | "special" | "long";
export type PowerUpKind = "jump" | "bumper" | "portal";
export type PowerUpSettings = Record<PowerUpKind, boolean>;

export const MAX_HOLES = 18;
export const DEFAULT_POWER_UPS: PowerUpSettings = {
  jump: true,
  bumper: true,
  portal: true,
};

export type PlayerStatus = {
  id: string;
  slot: PlayerSlot;
  name: string;
  connected: boolean;
  ready: boolean;
  color: string;
  scores: number[];
  currentHoleComplete: boolean;
  strokesThisHole: number;
};

export type RoomConfig = {
  gameId?: GameId;
  holeCount: number;
  seed: string;
  holeIds?: number[];
  playMode: PlayMode;
  coursePack: CoursePack;
  powerUps: PowerUpSettings;
};

export type RoomSnapshot = {
  type: "room_state";
  code: string;
  config: RoomConfig;
  hostId: string;
  players: PlayerStatus[];
  currentHole: number;
  phase: "lobby" | "playing" | "finished";
  createdAt: number;
};

export type ServerMessage =
  | RoomSnapshot
  | {
      type: "match_found";
      code: string;
      gameId?: GameId;
      holeCount: number;
      playMode?: PlayMode;
      coursePack?: CoursePack;
      powerUps?: PowerUpSettings;
    }
  | {
      type: "queued";
      position: number;
    }
  | {
      type: "player_joined";
      player: PlayerStatus;
    }
  | {
      type: "player_left";
      playerId: string;
    }
  | {
      type: "shot";
      playerId: string;
      slot: PlayerSlot;
      hole: number;
      stroke: number;
      start: Point;
      velocity: Point;
      shotId: string;
      serverAt: number;
    }
  | {
      type: "ball_state";
      playerId: string;
      slot: PlayerSlot;
      hole: number;
      pos: Point;
      vel: Point;
      moving: boolean;
      at: number;
    }
  | {
      type: "game_event";
      playerId: string;
      slot: PlayerSlot;
      gameId?: GameId;
      event: unknown;
      serverAt: number;
    }
  | {
      type: "game_score";
      playerId: string;
      slot: PlayerSlot;
      hole: number;
      score: number;
      complete: boolean;
      scorecard: number[];
    }
  | {
      type: "hole_complete";
      playerId: string;
      slot: PlayerSlot;
      hole: number;
      strokes: number;
      scorecard: number[];
    }
  | {
      type: "hole_advance";
      hole: number;
      phase: "playing" | "finished";
    }
  | {
      type: "error";
      message: string;
    };

export type ClientMessage =
  | {
      type: "set_name";
      name: string;
    }
  | {
      type: "ready";
    }
  | {
      type: "start";
    }
  | {
      type: "shot";
      hole: number;
      stroke: number;
      start: Point;
      velocity: Point;
      shotId: string;
    }
  | {
      type: "ball_state";
      hole: number;
      pos: Point;
      vel: Point;
      moving: boolean;
    }
  | {
      type: "game_event";
      gameId?: GameId;
      event: unknown;
    }
  | {
      type: "game_score";
      hole: number;
      score: number;
      complete?: boolean;
    }
  | {
      type: "hole_complete";
      hole: number;
      strokes: number;
    }
  | {
      type: "restart";
      holeCount?: number;
      coursePack?: CoursePack;
      powerUps?: PowerUpSettings;
    };

export type Point = {
  x: number;
  y: number;
};

export function clampHoleCount(value: number): number {
  if (!Number.isFinite(value)) return 5;
  return Math.min(MAX_HOLES, Math.max(1, Math.round(value)));
}

export function normalizePlayMode(value: unknown): PlayMode {
  return value === "race" ? "race" : "strokes";
}

export function normalizeCoursePack(value: unknown): CoursePack {
  if (value === "long") return "long";
  return value === "special" ? "special" : "classic";
}

export function normalizePowerUps(value: unknown): PowerUpSettings {
  if (value === false || value === "false" || value === "off" || value === 0) {
    return { jump: false, bumper: false, portal: false };
  }
  if (!value || value === true || value === "true" || value === "on" || value === 1) {
    return { ...DEFAULT_POWER_UPS };
  }
  if (typeof value === "string") {
    try {
      return normalizePowerUps(JSON.parse(value));
    } catch {
      return { ...DEFAULT_POWER_UPS };
    }
  }
  if (typeof value !== "object") return { ...DEFAULT_POWER_UPS };
  const settings = value as Partial<Record<PowerUpKind, unknown>>;
  return {
    jump: normalizePowerUpFlag(settings.jump),
    bumper: normalizePowerUpFlag(settings.bumper),
    portal: normalizePowerUpFlag(settings.portal),
  };
}

function normalizePowerUpFlag(value: unknown): boolean {
  return value === false || value === "false" || value === "off" || value === 0 ? false : true;
}

export function isPowerUpEnabled(settings: PowerUpSettings, kind: PowerUpKind): boolean {
  return settings[kind] !== false;
}
